import logging from '@sstdev/lib_logging';
import globalConfig from '../globalConfig/index';
import * as bridge from './bridgeLogger';
import constants from '../constants';
import loggingSessionObserver from '../loggingSessionObserver';

/**
 * Applies the current session to the shared logger, then notifies any
 * platform-specific observer of the same session change.
 *
 * The notification runs last so native observability receives the session only
 * after the shared logger has updated its environment, release, tenant, user,
 * use-case, and sampling context. Web callers retain the no-op observer.
 *
 * @param {object|null|undefined} session Current application session. An empty
 * session clears session-derived context.
 * @returns {void}
 */
function configureLogging(session) {
    const config = globalConfig();
    logging.setMinLogLevel(config.minLogLevel);
    if (config.logType & logging.outputType.BRIDGE) {
        logging.setBridgeLogger(bridge);
    }
    logging.setOutputType(config.logType);
    logging.setEnvironment(config.environment);
    logging.setRelease(config.logRelease);
    logging.setTenantId(session?.tenant?.['identity:tenant']?._id ?? '?');
    logging.setUserId(session?.profile?.userId ?? '?');
    logging.setUseCaseTitle(session?.useCase?.['metaui:useCase']?.title ?? '?');

    const ucValues = overrides[session?.useCase?.['metaui:useCase']?.id] ?? overrides.default;
    logging.setSampleRate(ucValues.sampleRate);
    logging.setIgnoredErrors(IGNORED_ERRORS);
    loggingSessionObserver.notify(session);
}

const overrides = {
    [constants.useCaseIds.FREIGHT_RUNNER]: {
        // https://docs.sentry.io/platforms/javascript/configuration/sampling/
        sampleRate: 0.25
    },
    default: {
        sampleRate: 1
    }
};

// ALL errors that include any of the following strings will be ignored, and not sent to Sentry:
// If you need to filter by exact match, use a regex pattern instead.
// https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-ignore-errors
const IGNORED_ERRORS = [
    // common login errors:
    'Unknown User or Incorrect Password',
    'Email is required',
    // user errors we don't care about
    'Unable to submit scan.',
    'Unable to delete built-in role',
    // logged multiple times in different ways:
    '[RE]Error: doingUpdate_application_useCase failed. - Error saving database',
    '[RE]Error: == Workflow failed for update_application_useCase == - Error saving database',
    'No data dictionary was found to validate the model',
    'No icon found with name'
];
export default configureLogging;
